import { useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { formatDistance } from 'date-fns';
import { Sentry } from '@/components/Sentry';
import { Grid, useMount, TabList, TabPanel, Tab } from '@simplywallst/ui-core';
import { Badge } from '@simplywallst/ui-core/next';

import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';
import { DemoHoldingsPanel } from '../../../../components/HoldingsPanel';
import { DividendDefaultError } from '../../../../components/DividendPanel/components/DividendDefaultError';
import { RenderUntilExpiry } from '@/components/RenderUntilExpiry';
import { AddPortfolioBanner } from './AddPortfolioBanner';
import { StyledHr } from './styled';
import { DemoNarrativePanel } from '@/features/portfolio/components/NarrativePanel/NarrativePanel';

const DividendPanel = loadable(
  () => import('../../../../components/DividendPanel'),
  {
    resolveComponent: (c) => c.DividendPanel,
  }
);

const DemoAnalysisPanel = loadable(
  () => import('../../../../components/AnalysisPanel'),
  {
    resolveComponent: (c) => c.DemoAnalysisPanel,
  }
);

type PortfolioTab = 'holdings' | 'analysis' | 'dividend' | 'narratives';

type Props = {
  portfolio: Portfolio;
};

export const PopulatedState = ({ portfolio }: Props) => {
  const [selectedPanel, setSelectedPanel] = useState<PortfolioTab>('holdings');

  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'populatedState',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  const timeSinceLastUpdate = useMemo(() => {
    return formatDistance(new Date(portfolio.updatedAt), new Date(), {
      addSuffix: true,
    });
  }, [portfolio.updatedAt]);

  return (
    <Track>
      <MiniPriceChartDefinitions />
      <div className="grid gap-x1 md:pb-x2">
        <div className="mx-x2 mt-x2 flex flex-wrap items-start justify-between gap-x2 lg:mx-x0">
          <div className="grid gap-x0_5">
            <PortfolioPageHeader portfolio={portfolio} className="px-x0" />
            <div className="text-tiny text-p-white/30">
              Updated{' '}
              <time className="text-p-white/50">{timeSinceLastUpdate}</time>
            </div>
          </div>
          <div className="grow md:max-w-fit">
            <AddPortfolioBanner />
          </div>
        </div>
        <div className="grid">
          <TabList templateColumns={`repeat(${5}, max-content)`}>
            <Tab<PortfolioTab>
              value="holdings"
              selected={selectedPanel === 'holdings'}
              onClick={() => setSelectedPanel('holdings')}
              styleType="subnav"
            >
              Holdings
            </Tab>
            <Tab<PortfolioTab>
              value="narratives"
              selected={selectedPanel === 'narratives'}
              onClick={() => setSelectedPanel('narratives')}
              styleType="subnav"
            >
              Narratives
            </Tab>
            <Tab<PortfolioTab>
              value="analysis"
              selected={selectedPanel === 'analysis'}
              onClick={() => setSelectedPanel('analysis')}
              styleType="subnav"
            >
              <Grid templateColumns="auto auto" alignItems="center">
                Analysis
              </Grid>
            </Tab>
            <Tab<PortfolioTab>
              value="dividend"
              selected={selectedPanel === 'dividend'}
              onClick={() => setSelectedPanel('dividend')}
              styleType="subnav"
            >
              <Grid templateColumns="auto auto" alignItems="center" gap="5px">
                Dividends
                {!portfolio.isAnalysisRestricted && (
                  <RenderUntilExpiry
                    startedFrom="2024-08-07"
                    expiresIn={{ months: 1 }}
                  >
                    <Badge tone="attention" size="small">
                      New
                    </Badge>
                  </RenderUntilExpiry>
                )}
              </Grid>
            </Tab>
          </TabList>
          <StyledHr />
        </div>
      </div>
      <TabPanel<PortfolioTab>
        value="holdings"
        selected={selectedPanel === 'holdings'}
      >
        <DemoHoldingsPanel portfolio={portfolio} />
      </TabPanel>
      <TabPanel<PortfolioTab>
        value="narratives"
        selected={selectedPanel === 'narratives'}
      >
        <DemoNarrativePanel portfolio={portfolio} />
      </TabPanel>
      <TabPanel<PortfolioTab>
        value="analysis"
        selected={selectedPanel === 'analysis'}
      >
        <DemoAnalysisPanel portfolio={portfolio} />
      </TabPanel>
      <TabPanel<PortfolioTab>
        value="dividend"
        selected={selectedPanel === 'dividend'}
      >
        <Sentry
          sentryCapture={{
            team: 'dingoes',
          }}
          customCopy={<DividendDefaultError />}
          globalDisabled
          isShownLogo={false}
          isShownHeading={false}
        >
          <DividendPanel portfolio={portfolio} />
        </Sentry>
      </TabPanel>
    </Track>
  );
};
